import {
  addDays,
  format,
  formatISO,
  isToday,
  isTomorrow,
  parseISO,
} from "date-fns";
import { useState } from "react";

import DatePicker, { type DateRangeType } from "@/components/date-picker";
import { getWeekRangeFromDate } from "@/components/date-picker/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { ChevronIcon, DayIcon, SwitchViewIcon, WeekIcon } from "../icons";

const FILTER_BUTTON_CLASS_NAME = cn(
  "line-clamp-1 flex h-10 items-center gap-2 rounded-[5px] border border-cool-gray/70 px-3 font-medium text-14 text-white shadow-white/20 transition-[colors,shadow] md:text-16",
  "hover:border-white/50 hover:bg-white/5 hover:shadow-0-0-10"
);

interface FiltersProps {
  isWeekView: boolean;
  onDateChange(date: Date): void;
  onViewChange(): void;
  selectedDate: Date;
}

export default function Filters({
  selectedDate,
  isWeekView,
  onDateChange,
  onViewChange,
}: FiltersProps) {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Calculate date range for display purposes
  const dateRange = isWeekView
    ? getWeekRangeFromDate(selectedDate)
    : { dateGte: selectedDate, dateLte: selectedDate };
  const { dateGte, dateLte } = dateRange;

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = parseISO(event.target.value);
    onDateChange(newDate);
  };

  const handleSwitchView = () => {
    onViewChange();
  };

  const handleCalendarDateChange = (newDateRange: DateRangeType) => {
    onDateChange(newDateRange.dateGte);
    setIsCalendarOpen(false);
  };

  let buttonLabel = `${format(dateGte, "MMM d")} - ${format(dateLte, "MMM d")}`;

  if (!isWeekView) {
    if (isToday(dateGte)) {
      buttonLabel = "Today";
    } else if (isTomorrow(dateGte)) {
      buttonLabel = "Tomorrow";
    } else {
      buttonLabel = format(dateGte, "EEEE");
    }

    buttonLabel += format(dateGte, ", MMM d");
  }

  return (
    <div className="mb-4 flex justify-between">
      <Input
        className="h-10 rounded-lg border border-white/10 px-3 py-2 font-medium text-16 text-white md:hidden"
        data-testid="date-input"
        max={formatISO(addDays(new Date(), 90), { representation: "date" })}
        min={formatISO(new Date(), { representation: "date" })}
        onChange={handleDateChange}
        type="date"
        value={formatISO(selectedDate, { representation: "date" })}
      />
      <Popover onOpenChange={setIsCalendarOpen} open={isCalendarOpen}>
        <PopoverTrigger
          className={cn(FILTER_BUTTON_CLASS_NAME, "group hidden md:flex")}
        >
          <SwitchViewIcon />
          {buttonLabel}
          <ChevronIcon className="transition-transform group-data-[state=open]:rotate-180" />
        </PopoverTrigger>
        <PopoverContent className="w-auto min-w-80 rounded-t-[10px] border-rich-black bg-rich-black p-5">
          <DatePicker
            dateRange={{ dateGte: selectedDate, dateLte: selectedDate }}
            futureDatesOnly
            isSingleDay
            limitToMonth
            onChange={handleCalendarDateChange}
          />
        </PopoverContent>
      </Popover>
      <Button
        className={cn(FILTER_BUTTON_CLASS_NAME, "ml-2.5 shrink-0")}
        onClick={handleSwitchView}
      >
        {isWeekView ? <DayIcon /> : <WeekIcon />}
        Show {isWeekView ? "day" : "week"}
      </Button>
    </div>
  );
}
